import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import AdminShell from "./AdminShell";
import { firestore } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

/* ---------- UI (nya designen) ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1.1fr 0.9fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const Title = styled.h3`
  margin: 0;
  font-weight: 950;
  color: #111827;
`;

const Tabs = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  appearance: none;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 950;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "#0f766e" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#111827")};

  &:hover {
    background: ${({ $active }) => ($active ? "#0f766e" : "#f9fafb")};
    transform: translateY(-1px);
  }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 13px;
  line-height: 1.45;
`;

const Preview = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 12px;
  background: #fafafa;

  p {
    margin: 0.35em 0;
  }
`;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

export default function AdminOpeningHours() {
  // 3 “lägen” du kan redigera:
  // folketsPark (öppettider), centrum (öppettider), content (informations-text)
  const [active, setActive] = useState("folketsPark"); // "folketsPark" | "centrum" | "content"
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // data i dokumentet settings/openingHours
  const [centrum, setCentrum] = useState("");
  const [folketsPark, setFolketsPark] = useState("");
  const [content, setContent] = useState("");

  const docRef = useMemo(() => doc(firestore, "settings", "openingHours"), []);

  const value = useMemo(() => {
    if (active === "centrum") return centrum;
    if (active === "content") return content;
    return folketsPark;
  }, [active, centrum, folketsPark, content]);

  const setValue = (v) => {
    if (active === "centrum") setCentrum(v);
    else if (active === "content") setContent(v);
    else setFolketsPark(v);
  };
const handleChange = useCallback(
  (v) => {
    if (active === "centrum") setCentrum(v);
    else if (active === "content") setContent(v);
    else setFolketsPark(v);
  },
  [active]
);
  const load = async () => {
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const d = snap.data() || {};
      setCentrum(d.centrum || "");
      setFolketsPark(d.folketsPark || "");
      setContent(d.content || "");
    } else {
      // skapa dokumentet om det saknas
      await setDoc(
        docRef,
        {
          centrum: "",
          folketsPark: "",
          content: "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setCentrum("");
      setFolketsPark("");
      setContent("");
    }
    setLoaded(true);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
  setBusy(true);
  try {
    const patch =
      active === "centrum"
        ? { centrum: centrum || "" }
        : active === "content"
        ? { content: content || "" }
        : { folketsPark: folketsPark || "" };

    await setDoc(
      docRef,
      {
        ...patch,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    alert(`Sparat: ${active}`);
  } catch (e) {
    alert(e?.message || "Kunde inte spara.");
  } finally {
    setBusy(false);
  }
};
const saveAll = async () => {
  setBusy(true);
  try {
    await setDoc(
      docRef,
      {
        centrum: centrum || "",
        folketsPark: folketsPark || "",
        content: content || "",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    alert("Sparat: allt");
  } catch (e) {
    alert(e?.message || "Kunde inte spara.");
  } finally {
    setBusy(false);
  }
};
  const reload = async () => {
    setBusy(true);
    try {
      await load();
      alert("Omladdat från databasen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title="Öppettider & Info">
      <Grid>
        <Card>
          <HeaderRow>
            <Title>Redigera</Title>
            <Tabs>
              <Tab
                type="button"
                $active={active === "folketsPark"}
                onClick={() => setActive("folketsPark")}
              >
                ⛳ Folkets Park
              </Tab>
              <Tab
                type="button"
                $active={active === "centrum"}
                onClick={() => setActive("centrum")}
              >
                🏌️ Centrum
              </Tab>
              <Tab
                type="button"
                $active={active === "content"}
                onClick={() => setActive("content")}
              >
                📝 Content (info)
              </Tab>
            </Tabs>
          </HeaderRow>

          {active !== "content" ? (
            <Muted style={{ marginBottom: 10 }}>
              Tips: skriv en rad per dag. Ex:
              <br />
              <code>
                {"<p>Mån 10–20</p><p>Tis 10–15</p><p>Ons 10–20</p><p>Sön: Stängt</p>"}
              </code>
            </Muted>
          ) : (
            <Muted style={{ marginBottom: 10 }}>
              Detta är “content”-texten som du har i samma dokument (info-text).
            </Muted>
          )}

          {loaded ? (
  <ReactQuill
    key={active}              // ✅ tvingar remount när du byter flik
    theme="snow"
    value={value}
    onChange={handleChange}   // ✅ uppdaterar alltid rätt fält
    modules={quillModules}
  />
) : (
  <Muted>Laddar…</Muted>
)}


         <BtnRow>
  <Btn type="button" onClick={save} disabled={busy || !loaded}>
    {busy ? "Sparar…" : "Spara denna"}
  </Btn>

  <Btn type="button" onClick={saveAll} disabled={busy || !loaded}>
    {busy ? "Sparar…" : "Spara allt"}
  </Btn>

  <Ghost type="button" onClick={reload} disabled={busy || !loaded}>
    Ladda om
  </Ghost>
</BtnRow>
        </Card>

        <Card>
          <Title>Förhandsvisning</Title>
          <div style={{ height: 10 }} />
          <Muted style={{ marginBottom: 10 }}>
            Visar exakt HTML som sparas i <b>{active}</b>.
          </Muted>

          <Preview>
            <div dangerouslySetInnerHTML={{ __html: value || "<p>Inget innehåll.</p>" }} />
          </Preview>
        </Card>
      </Grid>
    </AdminShell>
  );
}
